import type { MapRef } from 'react-map-gl/maplibre';
import { LngLat, LngLatLike, LngLatBounds } from 'maplibre-gl'
import { MutableRefObject} from 'react'
import { Board } from '../utils/Primitives';
import { getObjectives } from './Objectives';
import { getPlaces } from '../places/Places';

export type objectiveSetupProps = {
    boardRef: MutableRefObject<Board>,
    mapRef?: MutableRefObject<MapRef | null>
}

export type setFocusedObjectiveProps = {
    boardRef: MutableRefObject<Board>,
    mapRef?: MutableRefObject<MapRef | null>;
    objectiveId: number;
    isFocused: boolean;
}

export function clearFocusedObjective(props: objectiveSetupProps) {
    const {boardRef, mapRef} = props
    if (!mapRef) {
        return;
    }
    if (!mapRef.current) {
        return;
    }
    const map = mapRef.current.getMap();
    if (!map) {
        return;
    }
    const foundObjectives = getObjectives({boardRef})
    for (const objectiveId in foundObjectives) {
        const objective = foundObjectives[objectiveId]
        if (!objective) {
            continue
        }
        const foundPlaces = getPlaces({boardRef, places: objective.places || {}})
        for (const placeId in foundPlaces) {
            const place = foundPlaces[placeId]
            if (!place) {
                continue
            }
            const highlightLayerId = `objective-place-highlight-layer-${place.id}`
            const highlightLayer = map.getLayer(highlightLayerId)
            if (highlightLayer != undefined) {
                map.setLayoutProperty(highlightLayerId, 'visibility', 'none')
            }
            const boundsLayerId = `objective-bounds-layer-${objective.id}`
            const boundsLayer = map.getLayer(boundsLayerId)
            if (boundsLayer != undefined) {
                map.setLayoutProperty(boundsLayerId, 'visibility', 'none')
            }
        }
    }
}

export function setFocusedObjective(props: setFocusedObjectiveProps) {
    const {boardRef, mapRef, objectiveId, isFocused} = props
    if (!mapRef) {
        return;
    }
    if (!mapRef.current) {
        return;
    }
    const map = mapRef.current.getMap();
    if (!map) {
        return;
    }

    const foundObjectives = getObjectives({boardRef})
    const objective = foundObjectives[objectiveId]
    if (!objective) {
        return
    }
    const foundPlaces = getPlaces({boardRef, places: objective.places || {}})
    for (const placeId in foundPlaces) {
        const place = foundPlaces[placeId]
        if (!place) {
            continue
        }
        const highlightLayerId = `objective-place-highlight-layer-${place.id}`
        const highlightLayer = map.getLayer(highlightLayerId)
        if (highlightLayer != undefined) {
            map.setLayoutProperty(highlightLayerId, 'visibility',  isFocused ? 'visible' : 'none')
        }
        const boundsLayerId = `objective-bounds-layer-${objective.id}`
        const boundsLayer = map.getLayer(boundsLayerId)
        if (boundsLayer != undefined) {
            // NB: Objective bounds are disabled: visibility none
            map.setLayoutProperty(boundsLayerId, 'visibility',  isFocused ? 'none' : 'none')
        }
    }
}

export function addAllObjectivesToMap(props: objectiveSetupProps) {
    const {boardRef, mapRef} = props
    if (!mapRef) {
        return;
    }
    if (!mapRef.current) {
        return;
    }
    const map = mapRef.current.getMap();
    if (!map) {
        return;
    }

    const foundObjectives = getObjectives({boardRef})
    for (const objectiveId in foundObjectives) {
        const objective = foundObjectives[objectiveId]
        if (!objective) {
            continue
        }
        if (!objective.bounds) {
            console.log(`objective ${objective.id} has no bounds`)
            continue
        }

        // Define the expansion amount for latitude and longitude
        let expandLat: number = 0.0033 /* your latitude expansion amount */;
        let expandLng: number = 0.0033 /* your longitude expansion amount */;

        // Retrieve the current southwestern and northeastern points
        let sw: LngLatLike = objective.bounds.getSouthWest();
        let ne: LngLatLike = objective.bounds.getNorthEast();

        // Create new expanded bounds
        let expandedBounds = new LngLatBounds(
            [sw.lng - expandLng, sw.lat - expandLat], // new southwestern point
            [ne.lng + expandLng, ne.lat + expandLat]  // new northeastern point
        );


        const boundingPathPoints: LngLat[] = [
            expandedBounds.getNorthWest(),  // topLeft,
            expandedBounds.getNorthEast(),  // topRight,
            expandedBounds.getSouthEast(),  // bottomRight,
            expandedBounds.getSouthWest(),  // bottomLeft
            expandedBounds.getNorthWest(),  // close loop,
        ]

        const formattedboundingPathPoints = boundingPathPoints.map(point => {
            return [point.lng, point.lat]
        })

        const foundPlaces = getPlaces({boardRef, places: objective.places || {}})
        if (!foundPlaces) {
            return; // Handle the case where foundPlaces is undefined
        }
        for (const placeId in foundPlaces) {
            const place = foundPlaces[placeId];
            if (!place) {
                continue;
            }
            if (!place.bounds) {
                console.log(`place ${place.id} has no bounds`)
                continue
            }

            const boundingPathPoints: LngLat[] = [
                place.bounds.getNorthWest(),  // topLeft,
                place.bounds.getNorthEast(),  // topRight,
                place.bounds.getSouthEast(),  // bottomRight,
                place.bounds.getSouthWest(),  // bottomLeft
                place.bounds.getNorthWest(),  // close loop,
            ]

            const formattedboundingPathPoints = boundingPathPoints.map(point => {
                return [point.lng, point.lat]
            })

            const sourceId = `objective-place-o-source-${place.id}`
            const src = map.getSource(sourceId)
            if (src == undefined) {
                map.addSource(sourceId, {
                    'type': 'geojson',
                    'data': {
                        'type': 'Feature',
                        'geometry': {
                            'type': 'LineString',
                            'coordinates': formattedboundingPathPoints
                        },
                        'properties': {
                            'id': place.id
                        }
                }})
            }
            const layerId = `object-place-layer-${place.id}`
            const highlightLayerId = `objective-place-highlight-layer-${place.id}`
            const layer = map.getLayer(layerId)
            if (layer == undefined) {
                map.addLayer({
                        'id': highlightLayerId,
                        'type': 'fill',
                        'source': sourceId,
                        'paint': {
                           'fill-color': '#2076f7',
                           'fill-opacity': 0.3,
                        },
                        'layout': {
                            'visibility': 'none',
                        },
                    })
            }



        }

        const sourceId = `objective-source-${objective.id}`
        const src = map.getSource(sourceId)
        if (src == undefined) {
            map.addSource(sourceId, {
                'type': 'geojson',
                'data': {
                    'type': 'Feature',
                    'geometry': {
                        'type': 'LineString',
                        'coordinates': formattedboundingPathPoints
                    },
                    'properties': {
                        'id': objective.id
                    }
            }})
        }
        const layerId = `objective-bounds-layer-${objective.id}`
        const layer = map.getLayer(layerId)
        if (layer == undefined) {
            map.addLayer({
                    'id': layerId,
                    'type': 'line',
                    'source': sourceId,
                    'paint': {
                        'line-color': '#2076f7',
                        'line-width': 2,
                        'line-opacity': 0.65,
                    },
                    'layout': {
                        'visibility': 'none',
                    },
                })
        }

    }

}